<template>
	<div class="paginated-list" :data-test="`${dataTest}-datalist`">
		<div class="paginated-list__content">
			<slot
				name="list-items"
				v-bind="{
					items,
					loading,
					page,
					itemsPerPage,
				}"
			>
				<!-- Default implementation if no slot provided -->
				<v-list v-if="items.length > 0" class="paginated-list__items">
					<v-list-item
						v-for="item in items"
						:key="item.id"
						:value="item"
						@click="$emit('item-click', item)"
					>
						{{ item.id }} - {{ getDisplayValue(item) }}
					</v-list-item>
				</v-list>
			</slot>
		</div>

		<div v-if="loading && !usePlaceHolderData" class="paginated-list__loading">
			<slot name="loading">
				<v-skeleton-loader
					:type="`list-item@${itemsPerPage}`"
				></v-skeleton-loader>
			</slot>
		</div>

		<div v-if="error" class="paginated-list__error">
			<slot name="error" :error="error">
				<v-alert type="error" variant="tonal" border="start">
					{{ error.message || 'An error occurred while loading data' }}
				</v-alert>
			</slot>
		</div>

		<div
			v-if="!loading && !error && items.length === 0"
			class="paginated-list__empty"
		>
			<slot name="empty">
				<v-list>
					<v-list-item class="v-list-item--nodata">
						<v-list-item-title>{{
							t('common.no_data_available')
						}}</v-list-item-title>
					</v-list-item>
				</v-list>
			</slot>
		</div>

		<div
			v-if="itemCount > 0 && computedPageCount > 1"
			class="paginated-list__pagination"
		>
			<div class="d-flex align-center justify-center">
				<!-- Items per page selector -->
				<div v-if="showItemsPerPageSelector" class="paginated-list__per-page">
					<v-select
						v-model="currentItemsPerPage"
						:items="itemsPerPageOptions"
						label="Items per page"
						density="compact"
						hide-details
						single-line
						variant="outlined"
						style="max-width: 120px"
					></v-select>
				</div>
				<v-pagination
					v-model="currentPage"
					:disabled="loading"
					:length="computedPageCount"
					:total-visible="totalVisible"
					density="comfortable"
					@update:model-value="handlePageChange"
				></v-pagination>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
	items: {
		type: Array,
		required: true,
	},
	itemCount: {
		type: Number,
		required: true,
	},
	page: {
		type: Number,
		required: true,
	},
	itemsPerPage: {
		type: Number,
		default: 25,
	},
	itemsPerPageOptions: {
		type: Array,
		default: () => [10, 25, 50, 100],
	},
	loading: {
		type: Boolean,
		default: false,
	},
	error: {
		type: Object,
		default: null,
	},
	showItemsPerPageSelector: {
		type: Boolean,
		default: false,
	},
	displayProperty: {
		type: String,
		default: 'name',
	},
	paginationText: {
		type: String,
		default: 'Showing {start}-{end} of {total} items',
	},
	totalVisible: {
		type: Number,
		default: 7,
	},
	usePlaceHolderData: {
		type: Boolean,
		default: false,
	},
	dataTest: {
		type: String,
		required: true,
	},
})

const emit = defineEmits(['update:page', 'update:items-per-page', 'item-click'])
const { t } = useI18n()

// Local state with two-way binding
const currentPage = computed({
	get: () => props.page,
	set: (value) => emit('update:page', value),
})

const currentItemsPerPage = computed({
	get: () => props.itemsPerPage,
	set: (value) => emit('update:items-per-page', value),
})

// Computed properties
const computedPageCount = computed(() => {
	return Math.ceil(props.itemCount / props.itemsPerPage) || 1
})

// Methods
function handlePageChange(newPage) {
	emit('update:page', newPage)
}

function getDisplayValue(item) {
	if (!item) return ''

	// Handle dot notation for nested properties
	if (props.displayProperty.includes('.')) {
		return props.displayProperty
			.split('.')
			.reduce(
				(obj, key) => (obj && obj[key] !== undefined ? obj[key] : ''),
				item
			)
	}

	return item[props.displayProperty] || item.id || ''
}

// Watch for changes in items per page to reset to first page
watch(currentItemsPerPage, () => {
	// Reset to first page when changing items per page
	if (currentPage.value !== 1) {
		currentPage.value = 1
	}
})
</script>

<style scoped>
.paginated-list {
	padding: 0 !important;
	display: flex;
	flex-direction: column;
	max-height: calc(100vh - 300px);
}

.paginated-list__content {
	display: flex;
	flex-direction: column;
	width: 100%;
	height: calc(100% - 250px);
	max-height: calc(100% - 1500px);
	overflow-y: scroll;
}

.paginated-list__pagination {
	flex: 0 0 auto;
}
</style>
